import type { UsageLimit, UsageLimitName } from "@/features/auth/api/usage";
import { useFeatureFlags } from "@/features/config/useFeatureFlags";
import { formatBytes } from "@/features/usage/constants";
import { cn } from "@/utils/cn";
import { Link } from "react-router-dom";

const approachingCopy = (name: UsageLimitName, limit: UsageLimit): string => {
  if (limit.total == null) return "";
  const remaining = Math.max(0, limit.total - limit.usage);
  if (name === "workspaceStorage") {
    return `Workspace storage is almost full (${formatBytes(remaining)} left)`;
  }
  if (name === "dailyMaps") {
    return remaining === 1
      ? "You have 1 map left"
      : `You have ${remaining} maps left`;
  }
  return remaining === 1
    ? "You have 1 query left"
    : `You have ${remaining} queries left`;
};

const hitCopy = (name: UsageLimitName): string => {
  if (name === "workspaceStorage") {
    return "Workspace storage is full. Delete documents before uploading more";
  }
  if (name === "dailyMaps") {
    return "You've reached your current map limit.";
  }
  return "You've reached your current query limit.";
};

export const UsageAlert = ({
  id,
  name,
  isAtLimit,
  isApproachingLimit,
  limit,
}: {
  id: string;
  name: UsageLimitName;
  isAtLimit: boolean;
  isApproachingLimit: boolean;
  limit: UsageLimit | undefined;
}) => {
  const { flags } = useFeatureFlags();
  if (!limit || (!isAtLimit && !isApproachingLimit)) return null;

  const message = isAtLimit ? hitCopy(name) : approachingCopy(name, limit);

  return (
    <div
      id={id}
      role={isAtLimit ? "alert" : "status"}
      className={cn(
        "rounded-lg border px-3 py-2 text-xs",
        isAtLimit
          ? "border-red-200 bg-red-50 text-red-800"
          : "border-amber-200 bg-amber-50 text-amber-900",
      )}
    >
      <p>
        {message}{" "}
        <Link
          to="/settings"
          className="font-medium underline decoration-current/40 underline-offset-2 hover:decoration-current"
        >
          View usage{isAtLimit && flags.billing ? " or upgrade" : ""}
        </Link>
        .
      </p>
    </div>
  );
};